import { useMemo } from 'react';

import type {
  DataTableExternalSelectionAdapter,
  DataTableFeatures,
  DataTableSelectionSnapshot
} from 'design-system';

import type { Row } from '@tanstack/react-table';

import type { CatalogItem } from '@/entities/catalog-item';
import { Permission } from '@/entities/permissions';
import type { SymbolKey } from '@/entities/symbol';
import { symbolKey } from '@/entities/symbol';
import { usePermission } from '@/features/_shared/user';
import {
  deleteSymbols,
  requestAddForwardCurves,
  requestForwardCurveSelection,
  requestSpotSelection,
  SELECTOR_MODES
} from '@/features/symbol-selector';
import type { AppDispatch } from '@/store';
import { shallowEqual, useAppDispatch, useAppSelector } from '@/store/hooks';

import { isRowRestricted } from '../lib/isRowRestricted';
import { parseForwardCurveRowToSymbol } from '../lib/parseForwardCurveRowToSymbol';

type CatalogRow = Row<DataTableFeatures, CatalogItem>;

interface CreateCatalogSelectionAdapterOptions {
  dispatch: AppDispatch;
  isForwardCurves: boolean;
  permissionGranted: boolean;
  selectedSymbols: SymbolKey[];
  selectorMode: SELECTOR_MODES;
}

function flattenRows(rows: CatalogRow[]): CatalogRow[] {
  return rows.flatMap(row => [row, ...flattenRows(row.subRows)]);
}

function deduplicateSymbols(symbols: SymbolKey[]): SymbolKey[] {
  const seen = new Set<string>();
  return symbols.filter(symbol => {
    const key = symbolKey(symbol);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function createCatalogSelectionAdapter({
  dispatch,
  isForwardCurves,
  permissionGranted,
  selectedSymbols,
  selectorMode
}: CreateCatalogSelectionAdapterOptions): DataTableExternalSelectionAdapter<CatalogItem> {
  const isLocked = isForwardCurves
    ? selectorMode === SELECTOR_MODES.SPOT
    : selectorMode === SELECTOR_MODES.FORWARD_CURVE;
  const selectedKeys = new Set(
    selectedSymbols.map(symbol => symbolKey(symbol, !isForwardCurves && isLocked))
  );
  const selectionKey = (symbol: SymbolKey) => symbolKey(symbol, !isForwardCurves && isLocked);
  const isSelected = (symbol: SymbolKey) => selectedKeys.has(selectionKey(symbol));
  const isRestricted = (row: CatalogRow) =>
    isRowRestricted(row, isForwardCurves, permissionGranted);
  const getTargets = (row: CatalogRow): SymbolKey[] => {
    if (isRestricted(row)) return [];
    if (isForwardCurves) return parseForwardCurveRowToSymbol(row);
    return row.original.Symbol ? [row.original.Symbol] : [];
  };
  const getSnapshot = (targets: SymbolKey[], disabled: boolean): DataTableSelectionSnapshot => {
    const selectedCount = targets.filter(isSelected).length;
    const checked = targets.length > 0 && selectedCount === targets.length;

    return {
      checked,
      indeterminate: selectedCount > 0 && !checked,
      disabled: disabled || targets.length === 0,
      locked: !disabled && targets.length > 0 && isLocked
    };
  };

  return {
    getRowState: row => {
      const restricted = isRestricted(row);
      return getSnapshot(restricted ? [] : getTargets(row), restricted);
    },
    getHeaderState: rows => {
      const targets = deduplicateSymbols(flattenRows(rows).flatMap(getTargets));
      return getSnapshot(targets, targets.length === 0);
    },
    toggleRow: row => {
      const targets = getTargets(row);
      if (!targets.length) return;

      if (isForwardCurves) {
        dispatch(requestForwardCurveSelection({ symbols: targets }));
      } else {
        dispatch(requestSpotSelection({ symbol: targets[0] }));
      }
    },
    toggleRows: rows => {
      const targets = deduplicateSymbols(flattenRows(rows).flatMap(getTargets));
      if (!targets.length) return;

      if (targets.every(isSelected)) {
        dispatch(deleteSymbols({ symbols: targets }));
      } else if (isForwardCurves) {
        dispatch(requestAddForwardCurves({ symbols: targets }));
      } else {
        dispatch(requestSpotSelection({ symbols: targets }));
      }
    }
  };
}

export function useCatalogSelectionAdapter(
  isForwardCurves: boolean
): DataTableExternalSelectionAdapter<CatalogItem> {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermission();
  const permissionGranted = hasPermission(Permission.ViewCatalogDetails);
  const { selectedSymbols, selectorMode } = useAppSelector(
    state => ({
      selectedSymbols: state.symbolSelector.symbols,
      selectorMode: state.symbolSelector.selectorMode
    }),
    shallowEqual
  );

  return useMemo(
    () =>
      createCatalogSelectionAdapter({
        dispatch,
        isForwardCurves,
        permissionGranted,
        selectedSymbols,
        selectorMode
      }),
    [dispatch, isForwardCurves, permissionGranted, selectedSymbols, selectorMode]
  );
}
